import React from 'react';
import type { BiomarkerMeta } from '../../data/biomarkerCatalogue';

/**
 * The long-form explanation of a single marker.
 *
 * The expanded row carries one sentence, which is the right amount for someone
 * scanning a panel of results. This is for the member who stops on one marker
 * and wants to know what it actually is — a question they currently have to
 * take to their care team, or to a search engine.
 *
 * Patterned on FamilyTreeMemberModal rather than the exported EventModal:
 * EventModal has no `role="dialog"`, no `aria-modal`, no Escape handler and no
 * focus return, and this opens over a page of someone's own health results.
 *
 * Content is gated by the caller on the same `copyStatus` as the short copy, so
 * an unapproved deep-dive cannot reach a member — see
 * docs/clinical/BIOMARKER_COPY_SIGNOFF.md.
 */
export default function BiomarkerLearnMore({
  meta,
  onClose,
}: {
  meta: BiomarkerMeta;
  onClose: () => void;
}) {
  const closeRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    // Focus lands on the way out, not on a field — the dialog is read, not filled.
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="biomarker-learn-more-title"
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      style={{ backgroundColor: 'rgba(48, 46, 40, 0.45)' }}
      onClick={onClose}
    >
      <div
        className="border w-full max-w-2xl overflow-hidden rounded-t-3xl md:rounded-3xl max-h-[85vh] flex flex-col"
        style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="flex items-start justify-between gap-4 px-6 py-5 border-b"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: '#F7F6F2' }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: '#5C1A1C' }}>
              {meta.domainLabel}
            </p>
            <h2
              id="biomarker-learn-more-title"
              className="text-xl font-medium"
              style={{ color: 'var(--melio-ink)' }}
            >
              {meta.label}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full px-3 py-1 text-sm border"
            style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-text-muted)' }}
          >
            Close
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          <p className="text-base leading-relaxed mb-4" style={{ color: 'var(--melio-text)' }}>
            {meta.explanation}
          </p>
          {meta.longExplanation && (
            <div className="text-[15px] leading-relaxed whitespace-pre-line" style={{ color: 'var(--melio-text)' }}>
              {meta.longExplanation}
            </div>
          )}

          {/* Not a disclaimer for its own sake. The panel is preventive, and a
              member reading about a marker is exactly the moment someone starts
              wondering whether to act on it alone. */}
          <p className="text-sm mt-6 pt-4 border-t" style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-text-muted)' }}>
            This explains what the marker is, not what your result means. Your care team
            reads your results in context — ask them anything you are unsure about.
          </p>
        </div>
      </div>
    </div>
  );
}
